import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useUnitsofMeasureStore } from "../../../store/masterDataStore";
import { UnitsofMeasureType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm: UnitsofMeasureType = {
  unitName: "",
  unitSymbol: "",
  unitType: "",
  description: "",
};

const UnitsofMeasureList: React.FC = () => {
  const [rowData, setRowData] = useState<UnitsofMeasureType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<UnitsofMeasureType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const {
    unitsofMeasure,
    loading,
    fetchAllUnitsofMeasure,
    createUnitsofMeasure,
    updateUnitsofMeasure,
    deleteUnitsofMeasure,
  } = useUnitsofMeasureStore();

  useEffect(() => {
    fetchAllUnitsofMeasure();
  }, [fetchAllUnitsofMeasure]);

  useEffect(() => {
    setRowData(unitsofMeasure ?? []);
  }, [unitsofMeasure]);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let response = false;
    if (isEditMode && editId) {
      response = await updateUnitsofMeasure({ ...formData }, editId);
    } else {
      response = await createUnitsofMeasure(formData);
    }

    if (response) {
      setModalOpen(false);
      setFormData(emptyForm);
      setEditId(null);
      setIsEditMode(false);
    }
  };

  const handleEdit = (data: UnitsofMeasureType) => {
    setIsEditMode(true);
    setFormData({
      unitName: data.unitName || "",
      unitSymbol: data.unitSymbol || "",
      unitType: data.unitType || "",
      description: data.description || "",
    });
    setEditId(data._id || "");
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this unit of measure?"
    );
    if (confirmed) {
      await deleteUnitsofMeasure(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData(emptyForm);
    setEditId(null);
    setIsEditMode(false);
  };

  const columnDefs = useMemo<any>(
    () => [
      {
        field: "unitName",
        headerName: "Unit Name",
        sortable: false,
        minWidth: 30,
        maxWidth: 150,
        flex: 0,
        headerStyle: {
          backgroundColor: "#e9f5ff",
        },
        filter: false,
        cellStyle: {
          textAlign: "left",
          padding: "10px 0 0 15px",
        },
      },
      {
        field: "description",
        headerName: "Description",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        tooltipField: "description",
      },
      {
        field: "unitSymbol",
        headerName: "Unit Symbol",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0,
        minWidth: 40,
        maxWidth: 90,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Unit of Measure"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Unit of Measure"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
        filter: false,
      },
    ],
    []
  );

  const defaultColDef = useMemo<any>(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo<any>(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Unit of Measure</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Unit of Measure
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">No data added yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        {/* Scrollable modal content */}
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Unit of Measure"
              : "Create New Unit of Measure"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="unitOfMeasureForm"
            className="flex flex-col gap-6 pb-20"
          >
            {/* Unit Name */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="unitName"
                className="text-sm font-medium text-gray-700"
              >
                Unit Name <span className="text-red-500">*</span>
              </label>
              <input
                id="unitName"
                name="unitName"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.unitName}
                onChange={handleChange}
                placeholder="Enter unit name"
                required
                aria-required="true"
                aria-label="Unit Name"
              />
            </div>

            {/* Unit Symbol */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="unitSymbol"
                className="text-sm font-medium text-gray-700"
              >
                Unit Symbol <span className="text-red-500">*</span>
              </label>
              <input
                id="unitSymbol"
                name="unitSymbol"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.unitSymbol}
                onChange={handleChange}
                placeholder="Enter unit symbol (e.g., L, kg, mL)"
                required
                aria-required="true"
                aria-label="Unit Symbol"
              />
            </div>

            {/* Unit Type */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="unitType"
                className="text-sm font-medium text-gray-700"
              >
                Unit Type
              </label>
              <select
                id="unitType"
                name="unitType"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.unitType || ""}
                onChange={handleChange}
                aria-label="Unit Type"
              >
                <option value="">Select Unit Type</option>
                <option value="Volume">Volume</option>
                <option value="Weight">Weight</option>
                <option value="Count">Count</option>
                <option value="Temperature">Temperature</option>
                <option value="Pressure">Pressure</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="description"
                className="text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description (optional)"
                rows={3}
                aria-label="Description"
              />
            </div>

            {/* Bottom spacer to avoid footer overlap */}
            <div className="h-20" />
          </form>
        </div>

        {/* Sticky footer */}
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={handleModalClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="unitOfMeasureForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Create"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default UnitsofMeasureList;
